import React, {
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";
import { type WrapPageElementNodeArgs } from "gatsby";

import Layout from "@components/layout";
import Loader from "@components/loader";
import Seo from "@components/seo";
import { AffiliateProvider } from "@hooks/affiliate";

// gatsby-plugin-react-i18next wraps each page as
//   I18nextProvider > I18nextContext.Provider > <Page>
// AffiliateProvider and Layout have to sit inside both providers so the
// header/footer can call useI18next(), so we rebuild that shell instead of
// wrapping `element` from the outside.
const wrapPageElement = ({
  element,
  props,
}: WrapPageElementNodeArgs): ReactElement => {
  // Pages rendered by the affiliate-redirect template (both the exact
  // /<prefix>/ dummy and the /__affiliate_route/<prefix> catch-all) are the
  // only ones whose pageContext carries `affiliateCode`. Use that to skip
  // header/footer so the template shows a clean full-screen loader during
  // the ~1 tick before it navigate()s to the canonical URL.
  const isAffiliateRedirect = Boolean(
    (props.pageContext as { affiliateCode?: string } | undefined)
      ?.affiliateCode,
  );

  if (
    isValidElement<{ children?: ReactElement }>(element) &&
    isValidElement<{ children?: ReactNode }>(element.props.children)
  ) {
    const i18nContext = element.props.children;
    return React.cloneElement(
      element,
      undefined,
      React.cloneElement(
        i18nContext,
        undefined,
        <AffiliateProvider>
          <Layout noLayout={isAffiliateRedirect}>
            {i18nContext.props.children}
          </Layout>
        </AffiliateProvider>,
      ),
    );
  }

  return (
    <AffiliateProvider>
      <Layout showHeader={false} {...props}>
        <Seo />
        <div className="inset-0 flex h-dvh w-full items-center justify-center">
          <Loader />
        </div>
      </Layout>
    </AffiliateProvider>
  );
};

export default wrapPageElement;
